import { motion } from 'framer-motion';
import { X, Upload, Image, Search, Grid, List, ExternalLink, Heart, Download } from 'lucide-react';
import { useState, useRef } from 'react';
import { mediaApi, UnsplashPhoto } from '../services/api';
import { getOptimizedImageUrl } from '../utils/imageHelpers';

// Temporary interface - will be replaced with proper API types
interface MediaFile {
  id: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  width?: number;
  height?: number;
  url: string;
  altText?: string;
  createdAt: string;
  uploadedBy?: {
    id: string;
    name: string;
    photo?: string;
  };
}

interface MediaSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (mediaFile: MediaFile) => void;
  selectedUrl?: string;
  title?: string;
}

// Temporary mock data - will be replaced with real API calls
const mockMediaFiles: MediaFile[] = [
  {
    id: '1',
    fileName: 'strategy-planning.jpg',
    originalName: 'strategy-planning.jpg',
    mimeType: 'image/jpeg',
    fileSize: 1024000,
    width: 1920,
    height: 1080,
    url: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&h=600&fit=crop',
    altText: 'Team working on strategy planning',
    createdAt: '2024-01-20T10:00:00Z',
    uploadedBy: {
      id: '1',
      name: 'John Doe',
      photo:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face',
    },
  },
  {
    id: '2',
    fileName: 'business-growth.jpg',
    originalName: 'business-growth.jpg',
    mimeType: 'image/jpeg',
    fileSize: 856000,
    width: 1600,
    height: 900,
    url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
    altText: 'Business growth analytics dashboard',
    createdAt: '2024-01-19T14:30:00Z',
    uploadedBy: {
      id: '2',
      name: 'Jane Smith',
    },
  },
  {
    id: '3',
    fileName: 'team-collaboration.jpg',
    originalName: 'team-collaboration.jpg',
    mimeType: 'image/jpeg',
    fileSize: 1200000,
    width: 1800,
    height: 1200,
    url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop',
    altText: 'Team collaboration in modern office',
    createdAt: '2024-01-18T09:15:00Z',
    uploadedBy: {
      id: '1',
      name: 'John Doe',
      photo:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face',
    },
  },
];

export default function MediaSelector({
  isOpen,
  onClose,
  onSelect,
  selectedUrl: _selectedUrl,
  title = 'Select Image',
}: MediaSelectorProps) {
  const [activeTab, setActiveTab] = useState<'uploads' | 'unsplash'>('uploads');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>(mockMediaFiles);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const [selectedUnsplashPhoto, setSelectedUnsplashPhoto] = useState<UnsplashPhoto | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Unsplash state
  const [unsplashPhotos, setUnsplashPhotos] = useState<UnsplashPhoto[]>([]);
  const [unsplashSearchTerm, setUnsplashSearchTerm] = useState('');
  const [isSearchingUnsplash, setIsSearchingUnsplash] = useState(false);
  const [unsplashPage, setUnsplashPage] = useState(1);
  const [unsplashTotal, setUnsplashTotal] = useState(0);
  const [hasMoreUnsplash, setHasMoreUnsplash] = useState(true);

  // Filter files based on search term
  const filteredFiles = mediaFiles.filter(
    (file) =>
      file.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.altText?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Only image files are allowed');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);

    try {
      // For now, create a temporary URL - this will be replaced with actual upload API
      const tempUrl = URL.createObjectURL(file);

      const newMediaFile: MediaFile = {
        id: Date.now().toString(),
        fileName: `${Date.now()}-${file.name}`,
        originalName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        url: tempUrl,
        altText: file.name.split('.')[0].replace(/[-_]/g, ' '),
        createdAt: new Date().toISOString(),
        uploadedBy: {
          id: 'current-user',
          name: 'Current User',
        },
      };

      // Add to the beginning of the files array
      setMediaFiles((prev) => [newMediaFile, ...prev]);

      // Automatically select the newly uploaded file
      setSelectedFile(newMediaFile);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Search Unsplash photos
  const searchUnsplash = async (query: string, page: number = 1, append: boolean = false) => {
    if (!query.trim()) return;

    setIsSearchingUnsplash(true);

    try {
      const response = await mediaApi.unsplash.search({
        query: query.trim(),
        page,
        per_page: 20,
        orientation: 'landscape', // Good for blog images
      });

      if (response.success && response.data) {
        const newPhotos = response.data.photos;
        setUnsplashPhotos((prev) => (append ? [...prev, ...newPhotos] : newPhotos));
        setUnsplashTotal(response.data.total);
        setHasMoreUnsplash(page < response.data.total_pages);
        setUnsplashPage(page);
      }
    } catch (error) {
      console.error('Error searching Unsplash:', error);
    } finally {
      setIsSearchingUnsplash(false);
    }
  };

  // Handle Unsplash search
  const handleUnsplashSearch = (query: string) => {
    setUnsplashSearchTerm(query);
    if (query.trim()) {
      searchUnsplash(query, 1, false);
    } else {
      setUnsplashPhotos([]);
      setUnsplashTotal(0);
    }
  };

  // Load more Unsplash photos
  const loadMoreUnsplash = () => {
    if (hasMoreUnsplash && !isSearchingUnsplash && unsplashSearchTerm.trim()) {
      searchUnsplash(unsplashSearchTerm, unsplashPage + 1, true);
    }
  };

  const handleSelect = () => {
    if (activeTab === 'uploads' && selectedFile) {
      onSelect(selectedFile);
      onClose();
    } else if (activeTab === 'unsplash' && selectedUnsplashPhoto) {
      // Convert Unsplash photo to MediaFile format
      const mediaFile: MediaFile = {
        id: selectedUnsplashPhoto.id,
        fileName: `unsplash-${selectedUnsplashPhoto.id}.jpg`,
        originalName:
          selectedUnsplashPhoto.description || `Photo by ${selectedUnsplashPhoto.user.name}`,
        mimeType: 'image/jpeg',
        fileSize: 0, // Unknown for Unsplash
        width: selectedUnsplashPhoto.width,
        height: selectedUnsplashPhoto.height,
        url: selectedUnsplashPhoto.urls.regular,
        altText:
          selectedUnsplashPhoto.description ||
          `Photo by ${selectedUnsplashPhoto.user.name} on Unsplash`,
        createdAt: new Date().toISOString(),
        uploadedBy: {
          id: selectedUnsplashPhoto.user.id,
          name: selectedUnsplashPhoto.user.name,
          photo: selectedUnsplashPhoto.user.profile_image,
        },
      };

      // Track download (required by Unsplash API)
      mediaApi.unsplash.trackDownload(selectedUnsplashPhoto.links.download_location);

      onSelect(mediaFile);
      onClose();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <div className="flex items-center space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
            {activeTab === 'uploads' && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span>{isUploading ? 'Uploading...' : 'Upload'}</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => {
              setActiveTab('uploads');
              setSelectedFile(null);
              setSelectedUnsplashPhoto(null);
            }}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'uploads'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Uploads
          </button>
          <button
            onClick={() => {
              setActiveTab('unsplash');
              setSelectedFile(null);
              setSelectedUnsplashPhoto(null);
            }}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'unsplash'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Unsplash Photos
          </button>
        </div>

        <div className="flex h-full max-h-[70vh]">
          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  {activeTab === 'uploads' ? (
                    <input
                      type="text"
                      placeholder="Search your images..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <input
                      type="text"
                      placeholder="Search Unsplash (e.g., mountains, business, technology)..."
                      value={unsplashSearchTerm}
                      onChange={(e) => handleUnsplashSearch(e.target.value)}
                      className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-80"
                    />
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {activeTab === 'uploads'
                    ? `${filteredFiles.length} image${filteredFiles.length !== 1 ? 's' : ''}`
                    : unsplashTotal > 0
                      ? `${unsplashTotal.toLocaleString()} Unsplash photos`
                      : isSearchingUnsplash
                        ? 'Searching...'
                        : 'Enter a search term'}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-blue-100 text-blue-600'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list'
                      ? 'bg-blue-100 text-blue-600'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Media Grid/List */}
            <div className="flex-1 overflow-y-auto p-4">
              {activeTab === 'uploads' ? (
                // Uploaded Files
                filteredFiles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <Image className="h-12 w-12 mb-4" />
                    <p className="text-lg font-medium">No images found</p>
                    <p className="text-sm">Upload your first image to get started</p>
                  </div>
                ) : (
                  <div
                    className={
                      viewMode === 'grid'
                        ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'
                        : 'space-y-2'
                    }
                  >
                    {filteredFiles.map((file) => (
                      <motion.div
                        key={file.id}
                        layout
                        className={`border rounded-lg overflow-hidden cursor-pointer transition-all ${
                          selectedFile?.id === file.id
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${viewMode === 'list' ? 'flex items-center p-3' : ''}`}
                        onClick={() => setSelectedFile(file)}
                      >
                        {viewMode === 'grid' ? (
                          <>
                            <div className="aspect-video relative bg-gray-100">
                              <img
                                src={getOptimizedImageUrl(file.url, { width: 400 })}
                                alt={file.altText || file.originalName}
                                className="w-full h-full object-cover"
                              />
                              {selectedFile?.id === file.id && (
                                <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-white rounded-full" />
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="p-3">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.originalName}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatFileSize(file.fileSize)} • {formatDate(file.createdAt)}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              <img
                                src={getOptimizedImageUrl(file.url, {
                                  width: 200,
                                  height: 200,
                                  fit: 'cover',
                                })}
                                alt={file.altText || file.originalName}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 ml-4">
                              <p className="text-sm font-medium text-gray-900">
                                {file.originalName}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatFileSize(file.fileSize)} • {formatDate(file.createdAt)}
                              </p>
                              {file.width && file.height && (
                                <p className="text-xs text-gray-400">
                                  {file.width} × {file.height}
                                </p>
                              )}
                            </div>
                            {selectedFile?.id === file.id && (
                              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white rounded-full" />
                              </div>
                            )}
                          </>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )
              ) : // Unsplash Photos
              unsplashPhotos.length === 0 && !isSearchingUnsplash ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <Search className="h-12 w-12 mb-4" />
                  <p className="text-lg font-medium">Search millions of high-quality photos</p>
                  <p className="text-sm">
                    Enter a search term to find professional photos from Unsplash
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div
                    className={
                      viewMode === 'grid'
                        ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'
                        : 'space-y-2'
                    }
                  >
                    {unsplashPhotos.map((photo) => (
                      <motion.div
                        key={photo.id}
                        layout
                        className={`border rounded-lg overflow-hidden cursor-pointer transition-all ${
                          selectedUnsplashPhoto?.id === photo.id
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${viewMode === 'list' ? 'flex items-center p-3' : ''}`}
                        onClick={() => setSelectedUnsplashPhoto(photo)}
                      >
                        {viewMode === 'grid' ? (
                          <>
                            <div className="aspect-video relative bg-gray-100">
                              <img
                                src={photo.urls.small}
                                alt={photo.description || `Photo by ${photo.user.name}`}
                                className="w-full h-full object-cover"
                              />
                              {selectedUnsplashPhoto?.id === photo.id && (
                                <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-white rounded-full" />
                                  </div>
                                </div>
                              )}
                              <div className="absolute bottom-2 left-2 flex items-center space-x-1 text-white text-xs">
                                {photo.user.profile_image && (
                                  <img
                                    src={photo.user.profile_image}
                                    alt={photo.user.name}
                                    className="w-4 h-4 rounded-full"
                                  />
                                )}
                                <span className="bg-black bg-opacity-50 px-1 rounded">
                                  {photo.user.name}
                                </span>
                              </div>
                            </div>
                            <div className="p-3">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {photo.description || `Photo by ${photo.user.name}`}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {photo.width} × {photo.height}
                                {photo.likes && (
                                  <span className="ml-2 inline-flex items-center">
                                    <Heart className="h-3 w-3 mr-1" />
                                    {photo.likes}
                                  </span>
                                )}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              <img
                                src={photo.urls.thumb}
                                alt={photo.description || `Photo by ${photo.user.name}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 ml-4">
                              <p className="text-sm font-medium text-gray-900">
                                {photo.description || `Photo by ${photo.user.name}`}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {photo.width} × {photo.height}
                              </p>
                              <div className="flex items-center space-x-2 mt-1">
                                {photo.user.profile_image && (
                                  <img
                                    src={photo.user.profile_image}
                                    alt={photo.user.name}
                                    className="w-4 h-4 rounded-full"
                                  />
                                )}
                                <span className="text-xs text-gray-400">{photo.user.name}</span>
                              </div>
                            </div>
                            {selectedUnsplashPhoto?.id === photo.id && (
                              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white rounded-full" />
                              </div>
                            )}
                          </>
                        )}
                      </motion.div>
                    ))}
                  </div>

                  {/* Load More Button */}
                  {hasMoreUnsplash && unsplashPhotos.length > 0 && (
                    <div className="flex justify-center pt-4">
                      <button
                        onClick={loadMoreUnsplash}
                        disabled={isSearchingUnsplash}
                        className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        {isSearchingUnsplash ? (
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        <span>{isSearchingUnsplash ? 'Loading...' : 'Load More'}</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - File Details */}
          {(selectedFile || selectedUnsplashPhoto) && (
            <div className="w-80 border-l border-gray-200 bg-gray-50 p-6 overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Image Details</h3>

              <div className="space-y-4">
                {/* Preview */}
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={selectedFile ? selectedFile.url : selectedUnsplashPhoto!.urls.regular}
                    alt={
                      selectedFile
                        ? selectedFile.altText || selectedFile.originalName
                        : selectedUnsplashPhoto!.description ||
                          `Photo by ${selectedUnsplashPhoto!.user.name}`
                    }
                    className="w-full h-full object-cover"
                  />
                </div>

                {selectedFile ? (
                  // Uploaded file details
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        File Name
                      </label>
                      <p className="text-sm text-gray-900">{selectedFile.originalName}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        File Size
                      </label>
                      <p className="text-sm text-gray-900">
                        {formatFileSize(selectedFile.fileSize)}
                      </p>
                    </div>

                    {selectedFile.width && selectedFile.height && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dimensions
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedFile.width} × {selectedFile.height} pixels
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Uploaded
                      </label>
                      <p className="text-sm text-gray-900">{formatDate(selectedFile.createdAt)}</p>
                    </div>

                    {selectedFile.uploadedBy && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Uploaded by
                        </label>
                        <div className="flex items-center space-x-2">
                          {selectedFile.uploadedBy.photo ? (
                            <img
                              src={selectedFile.uploadedBy.photo}
                              alt={selectedFile.uploadedBy.name}
                              className="w-6 h-6 rounded-full"
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-300 rounded-full" />
                          )}
                          <span className="text-sm text-gray-900">
                            {selectedFile.uploadedBy.name}
                          </span>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alt Text
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedFile.altText || 'No alt text provided'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                      <p className="text-xs text-gray-600 break-all font-mono bg-gray-100 p-2 rounded">
                        {selectedFile.url}
                      </p>
                    </div>
                  </div>
                ) : (
                  selectedUnsplashPhoto && (
                    // Unsplash photo details
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedUnsplashPhoto.description ||
                            `Photo by ${selectedUnsplashPhoto.user.name}`}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dimensions
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedUnsplashPhoto.width} × {selectedUnsplashPhoto.height} pixels
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Photographer
                        </label>
                        <div className="flex items-center space-x-2">
                          {selectedUnsplashPhoto.user.profile_image ? (
                            <img
                              src={selectedUnsplashPhoto.user.profile_image}
                              alt={selectedUnsplashPhoto.user.name}
                              className="w-6 h-6 rounded-full"
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-300 rounded-full" />
                          )}
                          <div>
                            <span className="text-sm text-gray-900 block">
                              {selectedUnsplashPhoto.user.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              @{selectedUnsplashPhoto.user.username}
                            </span>
                          </div>
                        </div>
                      </div>

                      {selectedUnsplashPhoto.likes && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Likes
                          </label>
                          <div className="flex items-center space-x-1">
                            <Heart className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-gray-900">
                              {selectedUnsplashPhoto.likes.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Source
                        </label>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-900">Unsplash</span>
                          <a
                            href={selectedUnsplashPhoto.links.html}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-800">
                          <strong>Attribution Required:</strong> When using this image, please
                          credit{' '}
                          <a
                            href={selectedUnsplashPhoto.user.links.html}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            {selectedUnsplashPhoto.user.name}
                          </a>{' '}
                          on{' '}
                          <a
                            href="https://unsplash.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            Unsplash
                          </a>
                        </p>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {activeTab === 'uploads' && selectedFile
              ? `Selected: ${selectedFile.originalName}`
              : activeTab === 'unsplash' && selectedUnsplashPhoto
                ? `Selected: ${selectedUnsplashPhoto.description || `Photo by ${selectedUnsplashPhoto.user.name}`}`
                : 'No image selected'}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedFile && !selectedUnsplashPhoto}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Select Image
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
